import { useState, useContext, useRef } from 'react';
import useHttp from './useHttp';
import { toast } from 'react-toastify';
import AuthContext from '../context/authContext';
import { currencyFormatter } from '../util/formatHelper';

function extractReferencePrimitive(value, apiConfig = {}) {
  if (value === null || value === undefined) return value;

  const candidateKeys = [
    apiConfig?.valueField,
    apiConfig?.idName,
    apiConfig?.itemField,
    '_id',
    'id',
    'value',
    'upgradeId',
  ].filter(Boolean);

  if (Array.isArray(value)) {
    return value.map(entry => extractReferencePrimitive(entry, apiConfig));
  }

  if (typeof value !== 'object') {
    return value;
  }

  for (const key of candidateKeys) {
    if (value?.[key] !== undefined && value?.[key] !== null && value?.[key] !== '') {
      return value[key];
    }
  }

  return value;
}

function shouldPreserveSelectableObjectShape(fieldSchema) {
  if (!fieldSchema || typeof fieldSchema !== 'object') return false;

  const idName = fieldSchema.api?.idName;
  const valueField = fieldSchema.api?.valueField;

  return Boolean(
    fieldSchema.api?.objectify ||
      (typeof idName === 'string' &&
        typeof valueField === 'string' &&
        idName.trim() &&
        valueField.trim() &&
        idName !== valueField)
  );
}

function sanitizePayloadBySchema(payload, currentSchema, mode = 'update') {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return payload;

  const sanitized = {};

  Object.entries(payload).forEach(([key, rawValue]) => {
    const fieldSchema = currentSchema?.[key];

    if (mode === 'create') {
      if (fieldSchema?.hideInCreate || fieldSchema?.excludeFromAdd || fieldSchema?.type === 'password') {
        return;
      }
    }

    if (!fieldSchema) {
      sanitized[key] = rawValue;
      return;
    }

    let nextValue = rawValue;

    if (fieldSchema.type === 'number') {
      if (rawValue === '' || (typeof rawValue === 'string' && rawValue.trim() === '')) {
        if (mode === 'update') {
          nextValue = null;
        } else {
          return;
        }
      } else if (typeof rawValue === 'string') {
        const asNumber = Number(rawValue);
        if (!Number.isNaN(asNumber)) {
          nextValue = asNumber;
        }
      }
    } else if (fieldSchema.type === 'select') {
      if (shouldPreserveSelectableObjectShape(fieldSchema)) {
        nextValue = rawValue;
      } else {
        nextValue = extractReferencePrimitive(rawValue, fieldSchema.api);
      }
    } else if (fieldSchema.type === 'multi-select') {
      if (shouldPreserveSelectableObjectShape(fieldSchema)) {
        nextValue = Array.isArray(rawValue) ? rawValue : rawValue ? [rawValue] : [];
      } else {
        const values = Array.isArray(rawValue) ? rawValue : rawValue ? [rawValue] : [];
        nextValue = values
          .map(entry => extractReferencePrimitive(entry, fieldSchema.api))
          .filter(entry => entry !== undefined && entry !== null && entry !== '');
      }
    } else if (
      fieldSchema.type === 'object' &&
      fieldSchema.schema &&
      rawValue &&
      typeof rawValue === 'object' &&
      !Array.isArray(rawValue)
    ) {
      nextValue = sanitizePayloadBySchema(rawValue, fieldSchema.schema, mode);
    } else if (fieldSchema.type === 'nested' && Array.isArray(rawValue) && fieldSchema.schema) {
      nextValue = rawValue.map(entry =>
        entry && typeof entry === 'object' && !Array.isArray(entry)
          ? sanitizePayloadBySchema(entry, fieldSchema.schema, mode)
          : entry
      );
    }

    sanitized[key] = nextValue;
  });

  return sanitized;
}

function collectDuplicateReferenceFields(currentSchema, parentPath = '') {
  if (!currentSchema || typeof currentSchema !== 'object') return [];

  return Object.entries(currentSchema).flatMap(([key, fieldSchema]) => {
    if (!fieldSchema) return [];

    const path = parentPath ? `${parentPath}.${key}` : key;
    const fields = [];

    if (
      (fieldSchema.type === 'select' || fieldSchema.type === 'multi-select') &&
      !shouldPreserveSelectableObjectShape(fieldSchema)
    ) {
      fields.push({
        path,
        valueKey:
          fieldSchema.api?.valueField || fieldSchema.api?.idName || fieldSchema.api?.itemField || '_id',
      });
    }

    if ((fieldSchema.type === 'object' || fieldSchema.type === 'nested') && fieldSchema.schema) {
      fields.push(...collectDuplicateReferenceFields(fieldSchema.schema, path));
    }

    return fields;
  });
}

export default function useEditableList(resourceType, schema, token, config = null) {
  const { sendRequest } = useHttp();
  const authCtx = useContext(AuthContext);
  const updateQueueRef = useRef(new Map()); // queueKey -> Promise
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    isFromServer: false,
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
    totalIsCapped: false,
  });

  const enqueueUpdate = async (queueKey, task) => {
    const map = updateQueueRef.current;
    const prev = map.get(queueKey) || Promise.resolve();
    const next = prev.catch(() => null).then(task);
    map.set(queueKey, next);
    try {
      return await next;
    } finally {
      if (map.get(queueKey) === next) {
        map.delete(queueKey);
      }
    }
  };

  const normalizePagination = p => {
    if (!p || typeof p !== 'object') return p;
    const totalPages =
      Number.isFinite(p.totalPages) ? p.totalPages : Number.isFinite(p.pages) ? p.pages : 0;
    return {
      isFromServer: true,
      page: p.page ?? 1,
      limit: p.limit ?? 20,
      total: p.total ?? 0,
      totalPages,
      totalIsCapped: Boolean(p.totalIsCapped),
    };
  };

  const sanitizePayload = (payload, mode = 'update') => {
    return sanitizePayloadBySchema(payload, schema, mode);
  };

  const fetchInitialData = async (page = 1, limit = 20, search = '', extraParams = {}) => {
    try {
      setIsLoading(true);
      const searchParam = config?.searchParam || 'search';
      const pageParam = config?.pageParam || 'page';
      const limitParam = config?.limitParam || 'limit';
      const endpoint = config?.endpoint || `/api/v1/${resourceType}`;
      const data = await sendRequest({
        url: endpoint,
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + token,
        },
        params: {
          [pageParam]: page,
          [limitParam]: limit,
          ...(search && { [searchParam]: search }),
          ...(extraParams || {}),
        },
      });

      // Handle both old format (array) and new format (object with pagination)
      if (Array.isArray(data)) {
        setPagination(prev => ({
          ...(prev || {}),
          isFromServer: false,
          page,
          limit,
        }));
        return data;
      } else if (data && data.pagination) {
        setPagination(normalizePagination(data.pagination));
        // Handle both singular and plural resource types
        if (config?.resultsKey && Array.isArray(data?.[config.resultsKey])) {
          return data[config.resultsKey];
        }
        const resourceKey = resourceType;
        const resourceKeyPlural = resourceType + 's';
        return (
          data[resourceKey] ||
          data[resourceKeyPlural] ||
          data.results ||
          data.customers ||
          data.users ||
          []
        );
      } else if (
        data &&
        typeof data === 'object' &&
        Array.isArray(data.docs) &&
        typeof data.totalDocs === 'number'
      ) {
        const pageNum = Number.isFinite(Number(data.page)) ? Number(data.page) : Number(page) || 1;
        const pageSize =
          Number.isFinite(Number(data.limit)) ? Number(data.limit) : Number(limit) || 20;
        const totalPages =
          Number.isFinite(Number(data.totalPages))
            ? Number(data.totalPages)
            : Number.isFinite(Number(data.pages))
              ? Number(data.pages)
              : pageSize > 0
                ? Math.ceil((data.totalDocs || 0) / pageSize)
                : 0;

        setPagination({
          isFromServer: true,
          page: pageNum,
          limit: pageSize,
          total: data.totalDocs || 0,
          totalPages,
        });

        return data.docs;
      } else if (
        data &&
        typeof data === 'object' &&
        typeof data.total === 'number' &&
        (Array.isArray(data.items) ||
          (config?.resultsKey && Array.isArray(data?.[config.resultsKey])))
      ) {
        const pageNum = Number.isFinite(Number(data.page)) ? Number(data.page) : Number(page) || 1;
        const pageSize =
          Number.isFinite(Number(data.pageSize)) ? Number(data.pageSize) : Number(limit) || 20;
        const totalPages = pageSize > 0 ? Math.ceil((data.total || 0) / pageSize) : 0;

        setPagination({
          isFromServer: true,
          page: pageNum,
          limit: pageSize,
          total: data.total || 0,
          totalPages,
        });

        if (config?.resultsKey && Array.isArray(data?.[config.resultsKey])) {
          return data[config.resultsKey];
        }
        if (Array.isArray(data.items)) return data.items;
        return [];
      } else {
        setPagination(prev => ({
          ...(prev || {}),
          isFromServer: false,
          page,
          limit,
        }));
        return data;
      }
    } catch (err) {
      setError(err.message);
      toast.error(`Fehler beim Laden der Daten: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch API options for all fields that need them (top-level and nested)
  const fetchApiOptions = async (extraParams = {}) => {
    const options = {};

    // Recursively collect fields with api.resource from schema and nested schemas
    const collectApiFields = sc => {
      const list = [];
      if (!sc || typeof sc !== 'object') return list;
      for (const [key, fieldSchema] of Object.entries(sc)) {
        if (fieldSchema && fieldSchema.api?.resource) {
          list.push([key, fieldSchema]);
        }
        if (fieldSchema && fieldSchema.type === 'nested' && fieldSchema.schema) {
          // merge nested results (note: keys may collide; last one wins)
          list.push(...collectApiFields(fieldSchema.schema));
        }
      }
      return list;
    };

    const apiFields = collectApiFields(schema);

    for (const [key, fieldSchema] of apiFields) {
      try {
        const url = `/api/v1/${fieldSchema.api.resource}`;
        const data = await sendRequest({
          url,
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + token,
          },
          params: { ...(extraParams || {}) },
        });

        // Case 1: simple array of strings → map to { value, label }
        if (Array.isArray(data) && data.every(it => typeof it === 'string')) {
          options[key] = data.map(str => ({ value: str, label: str }));
          continue;
        }

        // Case 2: array of objects
        if (Array.isArray(data)) {
          options[key] = data.map(item => {
            const valueField = fieldSchema.api.valueField || '_id';
            const labelField = fieldSchema.api.labelField || valueField;
            const value = item?.[valueField] ?? item;
            const baseLabel = item?.[labelField] ?? String(value ?? '');
            const apiResource = fieldSchema.api.resource;

            let label = baseLabel;
            let displayLabel = baseLabel;
            let secondaryLabel = '';
            const extra = {};

            if (apiResource === 'upgrade') {
              const name = String(item?.name ?? baseLabel ?? '');
              const rawPrice = item?.price;
              const price = typeof rawPrice === 'number' ? rawPrice : Number(rawPrice);
              const priceLabel = Number.isFinite(price) ? currencyFormatter.format(price) : '';

              label = name;
              displayLabel = priceLabel ? `${name} — ${priceLabel}` : name;
              secondaryLabel = String(item?.descriptionShort ?? '');
              if (Number.isFinite(price)) extra.price = price;
            }

            if (typeof fieldSchema.api.labelBuilder === 'function') {
              try {
                const built = fieldSchema.api.labelBuilder(item);
                if (built) displayLabel = built;
              } catch (_) {}
            }

            return { value, label, displayLabel, secondaryLabel, ...extra };
          });
          continue;
        }

        // Fallback
        options[key] = [];
      } catch (err) {
        toast.error(
          `Fehler beim Laden der Optionen für ${fieldSchema.label || key}: ${err.message}`
        );
      }
    }

    return options;
  };

  // Dynamically fetch options based on a related resource id (e.g., product -> pricing lists)
  // apiConfig supports:
  // - resourceById: base resource, e.g. 'product'
  // - dependentIdField: field name on parent item that holds the related id (default 'productId')
  // - listEndpoint: optional sub-endpoint (e.g., 'pricing')
  // - path: optional path inside returned object to extract array (fallback if no listEndpoint)
  // - labelField/valueField: mapping fields
  const fetchOptionsByResourceId = async (apiConfig, parentItem) => {
    try {
      if (!apiConfig || !apiConfig.resourceById || !parentItem) return [];
      const idField = apiConfig.dependentIdField || 'productId';
      const targetId = parentItem[idField];
      if (!targetId) return [];

      const url = apiConfig.listEndpoint
        ? `/api/v1/${apiConfig.resourceById}/${targetId}/${apiConfig.listEndpoint}`
        : `/api/v1/${apiConfig.resourceById}/${targetId}`;

      const data = await sendRequest({
        url,
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + token,
        },
      });

      const container = apiConfig.listEndpoint
        ? data
        : apiConfig.path
          ? data?.[apiConfig.path]
          : data;
      const arr = Array.isArray(container) ? container : [];
      const labelField = apiConfig.labelField || 'name';
      const valueField = apiConfig.valueField || '_id';
      return arr.map(item => ({
        value: item?.[valueField],
        label: item?.[labelField] ?? String(item?.[valueField] ?? ''),
      }));
    } catch (err) {
      // Missing referenced resource is expected in some older records; avoid noisy toasts.
      if (/^404:/.test(String(err?.message || ''))) {
        return [];
      }
      toast.error('Fehler beim Laden abhängiger Optionen');
      return [];
    }
  };

  const addItem = async (newItem, nestedEndpoint, mode = 'create') => {
    const toastMessage = `Die Anfrage zum Erstellen eines neuen Eintrags (${resourceType}) wurde gespeichert und kann später bestätigt werden`;
    const successToastMessage = 'Eintrag erfolgreich erstellt';
    const displayInfo = { Typ: `${resourceType} erstellen` };
    const sanitizedNewItem = sanitizePayload(newItem, mode);
    Object.entries(sanitizedNewItem || {}).forEach(([k, v]) => {
      const val = typeof v === 'object' ? JSON.stringify(v) : String(v);
      displayInfo[k] = val.length > 120 ? val.slice(0, 117) + '…' : val;
    });
    try {
      const endpoint = nestedEndpoint || `/api/v1/${resourceType}`;
      const response = await sendRequest(
        {
          url: endpoint,
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + token,
          },
          body: sanitizedNewItem,
        },
        toastMessage,
        null,
        displayInfo,
        null,
        successToastMessage
      );

      if (response && (response.status === 'success' || response._id || Array.isArray(response))) {
        if (authCtx?.preferences?.showSuccessToastsInDataViews !== false) {
          toast.success('Eintrag erfolgreich erstellt');
        }
        return response;
      } else if (response === null) {
        // Request was stored due to insufficient rights
        return null; // Return null but don't show error toast
      } else {
        const errMsg = response?.message || 'Aktion fehlgeschlagen';
        toast.error(errMsg);
        return false;
      }
    } catch (err) {
      // For other errors, show the error message
      const errorMessage = err.response?.data?.message || err.message;
      toast.error(`Fehler beim Erstellen des Eintrags: ${errorMessage}`);
      return false;
    }
  };

  const duplicateItemPayload = async sourceItem => {
    try {
      const duplicateContext = {
        referenceFields: collectDuplicateReferenceFields(schema),
      };
      const response = await sendRequest({
        url: '/api/v1/editableList/duplicate',
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + token,
        },
        body: { item: sourceItem, duplicateContext },
      });

      if (response && typeof response === 'object' && !Array.isArray(response)) {
        return response;
      }

      const error = new Error('The duplicate payload could not be created.');
      error.code = 'EDITABLE_LIST_DUPLICATE_INVALID_RESPONSE';
      throw error;
    } catch (err) {
      throw err;
    }
  };

  // TODO this can be simplified (if conditions are messy etc)
  const updateItem = async (itemId, updates, nestedEndpoint) => {
    const queueKey = nestedEndpoint
      ? `PATCH:${String(nestedEndpoint).split('?')[0]}`
      : `PATCH:/api/v1/${resourceType}/${itemId}`;
    const toastMessageUpdate = `Die Anfrage zum Aktualisieren eines Eintrags (${resourceType}) wurde gespeichert und kann später bestätigt werden`;
    const successToastMessage = 'Änderungen erfolgreich gespeichert';
    const displayInfoUpdate = { Typ: `${resourceType} aktualisieren`, ID: itemId };
    const sanitizedUpdates = sanitizePayload(updates, 'update');
    Object.entries(sanitizedUpdates || {}).forEach(([k, v]) => {
      const val = typeof v === 'object' ? JSON.stringify(v) : String(v);
      displayInfoUpdate[k] = val.length > 120 ? val.slice(0, 117) + '…' : val;
    });
    return await enqueueUpdate(queueKey, async () => {
      try {
        if (nestedEndpoint) {
          const response = await sendRequest(
            {
              url: nestedEndpoint,
              method: 'PATCH',
              headers: {
                'Content-Type': 'application/json',
                Authorization: 'Bearer ' + token,
              },
              body: sanitizedUpdates,
            },
            toastMessageUpdate,
            null,
            displayInfoUpdate,
            null,
            successToastMessage
          );

          if (
            response &&
            (response.status === 'success' || response._id || Array.isArray(response))
          ) {
            if (authCtx?.preferences?.showSuccessToastsInDataViews !== false) {
              toast.success('Änderungen erfolgreich gespeichert');
            }
            return response;
          } else if (response === null) {
            // Request was stored due to insufficient rights
            return false; // Return false but don't show error toast
          }
          toast.error(response?.message || 'Aktion fehlgeschlagen');
          return false;
        }

        // Handle special fields that need separate API calls
        const specialFields = Object.entries(schema).filter(
          ([_, fieldSchema]) => fieldSchema.api?.resource && !fieldSchema.api.skipSpecial
        );

        for (const [key, fieldSchema] of specialFields) {
          if (sanitizedUpdates[key]) {
            const response = await sendRequest(
              {
                url: `/api/v1/${resourceType}/${itemId}/${fieldSchema.api.resource}`,
                method: 'PATCH',
                headers: {
                  'Content-Type': 'application/json',
                  Authorization: 'Bearer ' + token,
                },
                body: { [fieldSchema.api.resource]: sanitizedUpdates[key] },
              },
              toastMessageUpdate,
              null,
              displayInfoUpdate,
              null,
              successToastMessage
            );

            if (
              response &&
              (response.status === 'success' || response._id || Array.isArray(response))
            ) {
              if (authCtx?.preferences?.showSuccessToastsInDataViews !== false) {
                toast.success('Änderungen erfolgreich gespeichert');
              }
              return response;
            } else if (response === null) {
              // Request was stored due to insufficient rights
              return false; // Return false but don't show error toast
            }
            toast.error(response?.message || 'Aktion fehlgeschlagen');
            return false;
          }
        }

        // If there are any remaining updates, process them through the general PATCH endpoint
        if (Object.keys(sanitizedUpdates).length > 0) {
          const response = await sendRequest(
            {
              url: `/api/v1/${resourceType}/${itemId}`,
              method: 'PATCH',
              headers: {
                'Content-Type': 'application/json',
                Authorization: 'Bearer ' + token,
              },
              body: sanitizedUpdates,
            },
            toastMessageUpdate,
            null,
            displayInfoUpdate,
            null,
            successToastMessage
          );

          if (
            response &&
            (response.status === 'success' || response._id || Array.isArray(response))
          ) {
            if (authCtx?.preferences?.showSuccessToastsInDataViews !== false) {
              toast.success('Änderungen erfolgreich gespeichert');
            }
            return response;
          } else if (response === null) {
            // Request was stored due to insufficient rights
            return false; // Return false but don't show error toast
          }
          toast.error(response?.message || 'Aktion fehlgeschlagen');
          return false;
        }
      } catch (err) {
        toast.error(`Fehler beim Speichern der Änderungen: ${err.message}`);
        return false;
      }
    });
  };

  const deleteItem = async (itemId, nestedEndpoint, callback = null) => {
    const toastMessage = `Die Anfrage zum Löschen eines Eintrags (${resourceType}) wurde gespeichert und kann später bestätigt werden`;
    const successToastMessage = 'Eintrag erfolgreich gelöscht';
    const displayInfo = { Typ: `${resourceType} löschen`, ID: itemId };

    try {
      const response = await sendRequest(
        {
          url: nestedEndpoint ? nestedEndpoint : `/api/v1/${resourceType}/${itemId}`,
          method: 'DELETE',
          headers: {
            Authorization: 'Bearer ' + token,
          },
        },
        toastMessage,
        callback,
        displayInfo,
        null,
        successToastMessage
      );

      // Check if the response indicates success
      if (response && (response.success === true || response.action === 'delete')) {
        if (authCtx?.preferences?.showSuccessToastsInDataViews !== false) {
          toast.success('Eintrag erfolgreich gelöscht');
        }
        return true;
      } else if (response === null) {
        // Request was stored due to insufficient rights
        return false; // Return false but don't show error toast
      } else {
        toast.error('Löschung fehlgeschlagen.');
        return false;
      }
    } catch (err) {
      // For other errors, show the error message
      const errorMessage = err.response?.data?.message || err.message;
      toast.error(`Fehler beim Löschen des Eintrags: ${errorMessage}`);
      return false;
    }
  };

  return {
    isLoading,
    error,
    pagination,
    addItem,
    duplicateItemPayload,
    updateItem,
    deleteItem,
    fetchInitialData,
    fetchApiOptions,
    fetchOptionsByResourceId,
  };
}
